/*
 * A tiny inline-SVG icon set. Text glyphs (★ ⋯ ▸ ↑ ✕) depend on the user's fonts: the app's own font
 * doesn't carry them, and a system without a fallback (headless Chrome, some Linux desktops) shows an
 * empty box instead. These draw with `currentColor` at 1em, so they follow the text around them.
 *
 * Every icon is `aria-hidden`: the button or link it sits in keeps its own accessible name.
 */

function Svg({ children, className = '', size = 'size-[1em]', strokeWidth = 1.8, fill = 'none' }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`inline-block shrink-0 align-[-0.125em] ${size} ${className}`}
      fill={fill}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const STAR = 'M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6Z';

/** ★ (filled) or ☆ (outline). */
export function StarIcon({ filled = false, ...props }) {
  return (
    <Svg {...props} strokeWidth={1.4} fill={filled ? 'currentColor' : 'none'}>
      <path d={STAR} />
    </Svg>
  );
}

export function ArrowUpIcon(props) {
  return (
    <Svg {...props}>
      <path d="M8 13V3M4 7l4-4 4 4" />
    </Svg>
  );
}

export function ArrowDownIcon(props) {
  return (
    <Svg {...props}>
      <path d="M8 3v10M4 9l4 4 4-4" />
    </Svg>
  );
}

export function ArrowLeftIcon(props) {
  return (
    <Svg {...props}>
      <path d="M13 8H3M7 4 3 8l4 4" />
    </Svg>
  );
}

export function ArrowRightIcon(props) {
  return (
    <Svg {...props}>
      <path d="M3 8h10M9 4l4 4-4 4" />
    </Svg>
  );
}

/** ↪ (a symbolic link). */
export function LinkArrowIcon(props) {
  return (
    <Svg {...props}>
      <path d="M3 3v4a3 3 0 0 0 3 3h7M10 7l3 3-3 3" />
    </Svg>
  );
}

/** › (disclosure, closed). Rotate it 90° for open. */
export function ChevronRightIcon(props) {
  return (
    <Svg {...props} strokeWidth={2}>
      <path d="M6 3.5 10.5 8 6 12.5" />
    </Svg>
  );
}

export function ChevronDownIcon(props) {
  return (
    <Svg {...props} strokeWidth={2}>
      <path d="M3.5 6 8 10.5 12.5 6" />
    </Svg>
  );
}

export function ChevronUpIcon(props) {
  return (
    <Svg {...props} strokeWidth={2}>
      <path d="M3.5 10 8 5.5 12.5 10" />
    </Svg>
  );
}

/** ⋯ (a row's menu). */
export function MoreIcon(props) {
  return (
    <Svg {...props} strokeWidth={0} fill="currentColor">
      <circle cx="3" cy="8" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="13" cy="8" r="1.4" />
    </Svg>
  );
}

/** × / ✕ (remove, close). */
export function CloseIcon(props) {
  return (
    <Svg {...props} strokeWidth={2}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </Svg>
  );
}

export function CheckIcon(props) {
  return (
    <Svg {...props} strokeWidth={2.2}>
      <path d="M3.5 8.5l3 3 6-7" />
    </Svg>
  );
}

/** The "?" of a help button. */
export function QuestionIcon(props) {
  return (
    <Svg {...props} strokeWidth={2}>
      <path d="M5.8 5.8a2.3 2.3 0 1 1 3.2 2.1c-.6.3-1 .8-1 1.5v.4" />
      <circle cx="8" cy="12.4" r=".6" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** ⋮⋮ (a drag handle). */
export function GripIcon(props) {
  return (
    <Svg {...props} strokeWidth={0} fill="currentColor">
      <circle cx="5.5" cy="3.5" r="1.2" />
      <circle cx="10.5" cy="3.5" r="1.2" />
      <circle cx="5.5" cy="8" r="1.2" />
      <circle cx="10.5" cy="8" r="1.2" />
      <circle cx="5.5" cy="12.5" r="1.2" />
      <circle cx="10.5" cy="12.5" r="1.2" />
    </Svg>
  );
}

/** A machine (two stacked server units): the tile of a node. */
export function NodeIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <rect x="2" y="2.5" width="12" height="4.5" rx="1.2" />
      <rect x="2" y="9" width="12" height="4.5" rx="1.2" />
      <path d="M4.5 4.75h.01M4.5 11.25h.01M8 4.75h3.5M8 11.25h3.5" strokeWidth={1.8} />
    </Svg>
  );
}

/** The "!" of a warning. */
export function AlertIcon(props) {
  return (
    <Svg {...props} strokeWidth={2.2}>
      <path d="M8 3v6" />
      <circle cx="8" cy="12.5" r=".7" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Two overlapping sheets: copy to the clipboard. */
export function CopyIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.6}>
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.6" />
      <path d="M10.5 3.6V3.3A1.3 1.3 0 0 0 9.2 2H3.3A1.3 1.3 0 0 0 2 3.3v5.9a1.3 1.3 0 0 0 1.3 1.3h.3" />
    </Svg>
  );
}

/** ▶ start. */
export function PlayIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.4} fill="currentColor">
      <path d="M5 3.2v9.6a.6.6 0 0 0 .9.5l7.4-4.8a.6.6 0 0 0 0-1L5.9 2.7a.6.6 0 0 0-.9.5Z" />
    </Svg>
  );
}

/** ■ stop. */
export function StopIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.4} fill="currentColor">
      <rect x="3.5" y="3.5" width="9" height="9" rx="1.5" />
    </Svg>
  );
}

/** ↻ restart. */
export function RestartIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.8}>
      <path d="M13 8a5 5 0 1 1-1.5-3.6" />
      <path d="M13 2.5v3h-3" />
    </Svg>
  );
}

/** The "i" of an info notice. */
export function InfoIcon(props) {
  return (
    <Svg {...props} strokeWidth={2.2}>
      <path d="M8 7v6" />
      <circle cx="8" cy="3.8" r=".7" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** A clock face: scheduled or ongoing maintenance. */
export function ClockIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.8}>
      <circle cx="8" cy="8" r="5.8" />
      <path d="M8 4.8V8l2.2 1.4" />
    </Svg>
  );
}

/** A padlock: the action asks for the password again, or the value is locked. */
export function LockIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.6}>
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </Svg>
  );
}

/** A database cylinder: database hosts. */
export function DatabaseIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <ellipse cx="8" cy="3.8" rx="5" ry="1.8" />
      <path d="M3 3.8v8.4c0 1 2.2 1.8 5 1.8s5-.8 5-1.8V3.8M3 8c0 1 2.2 1.8 5 1.8S13 9 13 8" />
    </Svg>
  );
}

/** A cloud: remote storage (backup destinations). */
export function CloudIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M4.5 12.5a3 3 0 0 1-.3-6 4 4 0 0 1 7.6-.9 3.4 3.4 0 0 1 .2 6.9Z" />
    </Svg>
  );
}

/** A key: API keys, Steam accounts. */
export function KeyIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.6}>
      <circle cx="5" cy="10.5" r="2.8" />
      <path d="M7 8.5 13 2.5M11 4.5l1.5 1.5M9.5 6l1.2 1.2" />
    </Svg>
  );
}

/** A sheet of paper: pages and documents. */
export function PageIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M4 1.8h5.2L12.5 5v8a1.2 1.2 0 0 1-1.2 1.2H4A1.2 1.2 0 0 1 2.8 13V3A1.2 1.2 0 0 1 4 1.8Z" />
      <path d="M9 1.8V5h3.5M5.3 8.5h5M5.3 11h3.5" />
    </Svg>
  );
}

/** A folder: folders in the file manager. */
export function FolderIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M1.8 4.2A1.2 1.2 0 0 1 3 3h3.2l1.5 1.6H13a1.2 1.2 0 0 1 1.2 1.2v6.2A1.2 1.2 0 0 1 13 13.2H3A1.2 1.2 0 0 1 1.8 12Z" />
    </Svg>
  );
}

const SHEET = 'M4 1.8h5.2L12.5 5v8a1.2 1.2 0 0 1-1.2 1.2H4A1.2 1.2 0 0 1 2.8 13V3A1.2 1.2 0 0 1 4 1.8Z';

/** A blank sheet: files without a more specific icon. */
export function FileIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d={SHEET} />
      <path d="M9 1.8V5h3.5" />
    </Svg>
  );
}

/** A sheet with brackets: config and code files. */
export function FileCodeIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d={SHEET} />
      <path d="M9 1.8V5h3.5M6.3 8 5 9.5 6.3 11M9.2 8l1.3 1.5L9.2 11" />
    </Svg>
  );
}

/** A box: archives (zip, tar.gz) and packaged files (jar). */
export function ArchiveIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M2 2.8h12v3H2ZM2.8 5.8v6.9A1.2 1.2 0 0 0 4 13.9h8a1.2 1.2 0 0 0 1.2-1.2V5.8M6.5 8.5h3" />
    </Svg>
  );
}

/** A picture: images. */
export function ImageIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <rect x="2" y="2.5" width="12" height="11" rx="1.2" />
      <circle cx="5.8" cy="6.2" r="1.1" />
      <path d="m2.5 12 3.8-3.6 2.6 2.4 1.9-1.7L13.6 12" />
    </Svg>
  );
}

/** A terminal window: logs. */
export function LogIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <rect x="2" y="2.5" width="12" height="11" rx="1.2" />
      <path d="m4.5 6 1.8 1.5-1.8 1.5M7.8 10.5h3.7" />
    </Svg>
  );
}

/** A megaphone: announcements. */
export function MegaphoneIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M2.5 6.2v3.6h2.3l6.7 3V3.2l-6.7 3Z" />
      <path d="M4.8 9.8 5.8 13.5M13.5 6.5v3" />
    </Svg>
  );
}

/** Four tiles: the admin dashboard. */
export function GridIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <rect x="2.2" y="2.2" width="4.8" height="4.8" rx="1" />
      <rect x="9" y="2.2" width="4.8" height="4.8" rx="1" />
      <rect x="2.2" y="9" width="4.8" height="4.8" rx="1" />
      <rect x="9" y="9" width="4.8" height="4.8" rx="1" />
    </Svg>
  );
}

/** A game controller: servers and games. */
export function GamepadIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M5 4.5h6a3.5 3.5 0 0 1 3.4 4.3l-.6 2.6a1.6 1.6 0 0 1-2.8.6L9.8 10.5H6.2L5 12a1.6 1.6 0 0 1-2.8-.6l-.6-2.6A3.5 3.5 0 0 1 5 4.5Z" />
      <path d="M5.5 6.6v2M4.5 7.6h2M10.5 7.2h.01M11.5 8.2h.01" />
    </Svg>
  );
}

/** Two people: users and roles. */
export function PeopleIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <circle cx="6" cy="5.2" r="2.4" />
      <path d="M1.8 13.5a4.2 4.2 0 0 1 8.4 0M10.5 3a2.4 2.4 0 0 1 0 4.6M12 9.6a4.2 4.2 0 0 1 2.2 3.9" />
    </Svg>
  );
}

/** A shop front: the customer area. */
export function StoreIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M2.5 6.5v7h11v-7M1.8 6.2 3 2.5h10l1.2 3.7a1.9 1.9 0 0 1-3.6.8 2.3 2.3 0 0 1-4.4 0 1.9 1.9 0 0 1-3.6-.8Z" />
      <path d="M6.5 13.5V10h3v3.5" />
    </Svg>
  );
}

/** A heartbeat line: monitoring (activity, jobs, health). */
export function PulseIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.6}>
      <path d="M1.5 8.5h3l1.8-4.5 3.2 8 1.8-3.5h3.2" />
    </Svg>
  );
}

/** A gear: settings and configuration. */
export function GearIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <circle cx="8" cy="8" r="2.2" />
      <circle cx="8" cy="8" r="4.6" />
      <path d="M8 1.8v1.6M8 12.6v1.6M14.2 8h-1.6M3.4 8H1.8M12.4 3.6l-1.1 1.1M4.7 11.3l-1.1 1.1M12.4 12.4l-1.1-1.1M4.7 4.7 3.6 3.6" />
    </Svg>
  );
}

/** A magnifying glass: search (the console's older logs). */
export function SearchIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.7}>
      <circle cx="7" cy="7" r="4.5" />
      <path d="m10.4 10.4 3.6 3.6" />
    </Svg>
  );
}

/** A prompt in a window: the console. */
export function TerminalIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <rect x="1.8" y="2.8" width="12.4" height="10.4" rx="1.5" />
      <path d="M4.5 6.5 6.5 8l-2 1.5M8 10h3.5" />
    </Svg>
  );
}

/** An arrow into a tray: download. */
export function DownloadIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.7}>
      <path d="M8 2v8M4.8 7 8 10.2 11.2 7M2.5 11.5v1.3a1.2 1.2 0 0 0 1.2 1.2h8.6a1.2 1.2 0 0 0 1.2-1.2v-1.3" />
    </Svg>
  );
}

/** Two sliders: display settings (the console's font size, timestamps, follow). */
export function SlidersIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.6}>
      <path d="M2 4.5h7M12.5 4.5H14M2 11.5h1.5M7 11.5h7" />
      <circle cx="10.8" cy="4.5" r="1.7" />
      <circle cx="5.2" cy="11.5" r="1.7" />
    </Svg>
  );
}

/** A puzzle piece: add-ons, mods and other game tools. */
export function PuzzleIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M2.5 5h3a1.5 1.5 0 1 1 3 0h3v3a1.5 1.5 0 1 1 0 3v2.5h-9V11a1.5 1.5 0 1 0 0-3Z" />
    </Svg>
  );
}

/** A bin with a lid: the file manager's Trash. */
export function TrashIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M2.5 4.2h11M6.2 4.2V2.8h3.6v1.4M3.8 4.2l.7 8.9a1.2 1.2 0 0 0 1.2 1.1h4.6a1.2 1.2 0 0 0 1.2-1.1l.7-8.9M6.6 7v4.6M9.4 7v4.6" />
    </Svg>
  );
}

/** A box with an arrow out of it: share with a link (shared logs). */
export function ShareIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.6}>
      <path d="M8 1.8v8M5 4.6 8 1.8l3 2.8M5.2 6.5H4a1.2 1.2 0 0 0-1.2 1.2v5.1A1.2 1.2 0 0 0 4 14h8a1.2 1.2 0 0 0 1.2-1.2V7.7A1.2 1.2 0 0 0 12 6.5h-1.2" />
    </Svg>
  );
}

/** A sheet with lines and a folded corner: an invoice or quote. */
export function InvoiceIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M3.5 1.8h6.2l2.8 2.8v9.6h-9Z" />
      <path d="M9.5 1.8v3h3M5.8 7.5h4.4M5.8 10h4.4M5.8 12.4h2.4" />
    </Svg>
  );
}

/** A payment card. */
export function CardIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <rect x="1.6" y="3.4" width="12.8" height="9.2" rx="1.5" />
      <path d="M1.6 6.4h12.8M4 10.2h2.6" />
    </Svg>
  );
}

/** A shopping cart. */
export function CartIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M1.5 2.2h2l1.6 7.6h7.3l1.4-5.4H4.2" />
      <circle cx="6" cy="13" r="1" />
      <circle cx="11.6" cy="13" r="1" />
    </Svg>
  );
}

/** A ticket stub: support tickets. */
export function TicketIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.5}>
      <path d="M1.8 4h12.4v2.4a1.6 1.6 0 0 0 0 3.2V12H1.8V9.6a1.6 1.6 0 0 0 0-3.2Z" />
      <path d="M10 4v1.4M10 7.3v1.4M10 10.6V12" />
    </Svg>
  );
}

/** A globe: domains. */
export function GlobeIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.4}>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M1.8 8h12.4M8 1.8c-2.2 2.6-2.2 9.8 0 12.4M8 1.8c2.2 2.6 2.2 9.8 0 12.4" />
    </Svg>
  );
}

/** A bar chart: reports. */
export function ChartIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.6}>
      <path d="M2 14h12M4 11.5V8M8 11.5V4M12 11.5V6.5" />
    </Svg>
  );
}

/** A box: products and services. */
export function BoxIcon(props) {
  return (
    <Svg {...props} strokeWidth={1.4}>
      <path d="M8 1.8 14 4.6v6.8L8 14.2 2 11.4V4.6Z" />
      <path d="M2 4.6 8 7.4l6-2.8M8 7.4v6.8" />
    </Svg>
  );
}
